import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDataProvider, useNotify, useGetIdentity } from "ra-core";
import {
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  format,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter,
  RefreshCw,
  Calendar,
  Users,
  List as ListIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Task, Sale, Deal, Contact, Company } from "../types";
import { AgendaDayView } from "./AgendaDayView";
import { AgendaWeekView } from "./AgendaWeekView";
import { AgendaMonthView } from "./AgendaMonthView";
import { AgendaListView } from "./AgendaListView";
import { ActivityDialog } from "./ActivityDialog";
import { ACTIVITY_TYPES } from "./activityTypes";
import {
  getConnectedGoogleUserBySalesId,
  connectGoogleOAuth,
  syncGoogleCalendarToCrm,
} from "../providers/google/googleIntegrationService";
import { addAppNotification } from "../notifications/notificationsStore";
import { cn } from "@/lib/utils";

const CACHED_TASKS_KEY = "dotus_crm_cached_tasks_v2";
let memoryCachedTasks: Task[] | null = null;

function getCachedTasks(): Task[] {
  if (memoryCachedTasks && memoryCachedTasks.length > 0) {
    return memoryCachedTasks;
  }
  try {
    const raw = localStorage.getItem(CACHED_TASKS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        memoryCachedTasks = parsed;
        return parsed;
      }
    }
  } catch {}
  return [];
}

function saveCachedTasks(taskList: Task[]): void {
  memoryCachedTasks = taskList;
  try {
    localStorage.setItem(CACHED_TASKS_KEY, JSON.stringify(taskList));
  } catch {}
}

export const AgendaPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();

  const [viewMode, setViewMode] = useState<"day" | "week" | "month" | "list">(
    "week",
  );
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [tasks, setTasks] = useState<Task[]>(() => getCachedTasks());
  const [loading, setLoading] = useState(() => getCachedTasks().length === 0);
  const tasksRef = useRef<Task[]>(tasks);
  tasksRef.current = tasks;

  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [selectedSalesId, setSelectedSalesId] = useState<string>("all");

  const [deals, setDeals] = useState<Deal[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    dataProvider
      .getList("sales", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "first_name", order: "ASC" },
        filter: {},
      })
      .then((res) => setSales(res.data as Sale[]))
      .catch(() => {});

    dataProvider
      .getList("deals", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      })
      .then((res) => setDeals((res.data || []) as Deal[]))
      .catch(() => {});

    dataProvider
      .getList("contacts", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "first_name", order: "ASC" },
        filter: {},
      })
      .then((res) => setContacts((res.data || []) as Contact[]))
      .catch(() => {});

    dataProvider
      .getList("companies", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      })
      .then((res) => setCompanies((res.data || []) as Company[]))
      .catch(() => {});
  }, [dataProvider]);

  const dealsMap = useMemo(() => {
    return Object.fromEntries(deals.map((d) => [String(d.id), d]));
  }, [deals]);

  const contactsMap = useMemo(() => {
    return Object.fromEntries(contacts.map((c) => [String(c.id), c]));
  }, [contacts]);

  const companiesMap = useMemo(() => {
    return Object.fromEntries(companies.map((c) => [String(c.id), c]));
  }, [companies]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "done">(
    "all",
  );
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [defaultDialogDate, setDefaultDialogDate] = useState<Date | undefined>(
    undefined,
  );

  const fetchTasks = useCallback(
    async (isSilent = false) => {
      try {
        if (!isSilent && tasksRef.current.length === 0) {
          setLoading(true);
        }
        const pageSize = 1000;
        const res = await dataProvider.getList("tasks", {
          pagination: { page: 1, perPage: pageSize },
          sort: { field: "data_inicio", order: "ASC" },
          filter: {},
        });
        let fetchedTasks = (res.data || []) as Task[];
        const total = res.total ?? fetchedTasks.length;
        if (total > pageSize) {
          const totalPages = Math.ceil(total / pageSize);
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p++) {
            pagePromises.push(
              dataProvider.getList("tasks", {
                pagination: { page: p, perPage: pageSize },
                sort: { field: "data_inicio", order: "ASC" },
                filter: {},
              }),
            );
          }
          const otherPages = await Promise.all(pagePromises);
          for (const pageRes of otherPages) {
            if (pageRes?.data) {
              fetchedTasks = fetchedTasks.concat(pageRes.data as Task[]);
            }
          }
        }
        setTasks(fetchedTasks);
        saveCachedTasks(fetchedTasks);
      } catch (err) {
        console.error("Erro ao carregar tarefas da agenda:", err);
      } finally {
        setLoading(false);
      }
    },
    [dataProvider],
  );

  useEffect(() => {
    const hasCache = getCachedTasks().length > 0;
    fetchTasks(hasCache);
  }, [fetchTasks]);

  const handleSyncGoogleCalendar = useCallback(
    async (silent = false) => {
      try {
        const salesToSync =
          selectedSalesId !== "all"
            ? sales.filter((s) => String(s.id) === String(selectedSalesId))
            : sales;

        const connectedToSync = salesToSync.filter((s) =>
          Boolean(getConnectedGoogleUserBySalesId(s.id)),
        );

        if (connectedToSync.length === 0) {
          if (!silent) {
            const targetName =
              selectedSalesId !== "all"
                ? sales.find((s) => String(s.id) === String(selectedSalesId))
                    ?.first_name || "o usuário selecionado"
                : "nenhum usuário";
            notify(
              `Google Agenda não está conectada para ${targetName}. Conecte a conta do Google primeiro.`,
              { type: "info" },
            );
          }
          return;
        }

        if (!silent) setSyncingGoogle(true);

        let totalImported = 0;
        let totalUpdated = 0;
        let totalDeleted = 0;
        const syncedNames: string[] = [];

        for (const sale of connectedToSync) {
          try {
            const res = await syncGoogleCalendarToCrm(dataProvider, {
              salesId: sale.id,
            });
            if (res.success) {
              totalImported += res.imported;
              totalUpdated += res.updated;
              totalDeleted += res.deleted;
              syncedNames.push(sale.first_name);
            }
          } catch (err) {
            console.error(
              `Erro ao sincronizar agenda de ${sale.first_name}:`,
              err,
            );
          }
        }

        if (totalImported > 0 || totalUpdated > 0 || totalDeleted > 0) {
          await fetchTasks(true);

          const parts = [];
          if (totalImported > 0) parts.push(`${totalImported} nova(s)`);
          if (totalUpdated > 0) parts.push(`${totalUpdated} atualizada(s)`);
          if (totalDeleted > 0) parts.push(`${totalDeleted} excluída(s)`);
          const detail = parts.length > 0 ? ` (${parts.join(", ")})` : "";

          // Registra no sino de notificações do painel lateral (sem gerar lembretes/popups na tela)
          addAppNotification({
            title: "Google Agenda atualizada",
            description: `${syncedNames.join(", ")}${detail}`,
            type: "calendar",
            link: "/agenda",
          });
        }

        if (!silent) {
          notify("Google Agenda sincronizada!", { type: "success" });
        }
      } catch {
        if (!silent)
          notify("Erro ao sincronizar com Google Agenda", { type: "error" });
      } finally {
        if (!silent) setSyncingGoogle(false);
      }
    },
    [dataProvider, fetchTasks, notify, selectedSalesId, sales],
  );

  // Sincronização automática em 2 vias:
  // 1. Ao abrir a tela
  // 2. Imediatamente ao voltar para a aba do CRM (ao alternar do Google Calendar de volta para cá)
  // 3. A cada 15 segundos em segundo plano
  useEffect(() => {
    const hasAnyConnected = sales.some((s) =>
      Boolean(getConnectedGoogleUserBySalesId(s.id)),
    );
    if (!hasAnyConnected) return;

    // Sincroniza imediatamente ao carregar
    handleSyncGoogleCalendar(true);

    const onWindowFocus = () => {
      handleSyncGoogleCalendar(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleSyncGoogleCalendar(true);
      }
    };

    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    const interval = setInterval(() => {
      handleSyncGoogleCalendar(true);
    }, 15000);

    return () => {
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(interval);
    };
  }, [handleSyncGoogleCalendar, sales]);

  // Navigation handlers
  const handlePrev = () => {
    if (viewMode === "day") {
      setCurrentDate((d) => subDays(d, 1));
    } else if (viewMode === "week") {
      setCurrentDate((d) => subWeeks(d, 1));
    } else {
      setCurrentDate((d) => subMonths(d, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === "day") {
      setCurrentDate((d) => addDays(d, 1));
    } else if (viewMode === "week") {
      setCurrentDate((d) => addWeeks(d, 1));
    } else {
      setCurrentDate((d) => addMonths(d, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleSelectSlot = (date: Date) => {
    setSelectedTask(null);
    setDefaultDialogDate(date);
    setOpenDialog(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setDefaultDialogDate(undefined);
    setOpenDialog(true);
  };

  const handleCreateNew = () => {
    setSelectedTask(null);
    setDefaultDialogDate(currentDate);
    setOpenDialog(true);
  };

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (
        selectedSalesId !== "all" &&
        String(task.sales_id) !== String(selectedSalesId)
      )
        return false;
      if (statusFilter === "pending" && task.done_date) return false;
      if (statusFilter === "done" && !task.done_date) return false;
      if (typeFilter !== "all" && task.type !== typeFilter) return false;
      if (priorityFilter !== "all" && task.prioridade !== priorityFilter)
        return false;
      return true;
    });
  }, [tasks, selectedSalesId, statusFilter, typeFilter, priorityFilter]);

  // Title formatting
  const getHeaderTitle = () => {
    if (viewMode === "day") {
      return format(currentDate, "EEEE, d 'de' MMMM 'de' yyyy", {
        locale: ptBR,
      });
    }
    if (viewMode === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `Semana de ${format(start, "d 'de' MMM", { locale: ptBR })} a ${format(
        end,
        "d 'de' MMM, yyyy",
        { locale: ptBR },
      )}`;
    }
    return format(currentDate, "MMMM 'de' yyyy", { locale: ptBR });
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1700px] mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agenda Global</h1>
          <p className="text-xs text-muted-foreground">
            Reuniões, chamadas contábeis, visitas e follow-ups comerciais
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* View mode toggle */}
          <div className="flex items-center rounded-lg border bg-muted/50 p-1">
            <button
              type="button"
              onClick={() => setViewMode("day")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                viewMode === "day"
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Dia
            </button>
            <button
              type="button"
              onClick={() => setViewMode("week")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                viewMode === "week"
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Semana
            </button>
            <button
              type="button"
              onClick={() => setViewMode("month")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                viewMode === "month"
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Mês
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === "list"
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ListIcon className="w-3 h-3" />
              Lista
            </button>
          </div>

          {(() => {
            const isAllMode = selectedSalesId === "all";
            const currentSale = sales.find(
              (s) => String(s.id) === String(selectedSalesId),
            );

            if (isAllMode) {
              const connectedCount = sales.filter((s) =>
                Boolean(getConnectedGoogleUserBySalesId(s.id)),
              ).length;
              const disconnectedSales = sales.filter(
                (s) => !getConnectedGoogleUserBySalesId(s.id),
              );

              return (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {connectedCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncGoogleCalendar()}
                      disabled={syncingGoogle}
                      className="gap-1.5 h-8.5 text-xs font-semibold border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 shadow-xs cursor-pointer"
                      title="Sincronizar agenda de todos os usuários conectados"
                    >
                      <RefreshCw
                        className={cn(
                          "w-3.5 h-3.5",
                          syncingGoogle && "animate-spin",
                        )}
                      />
                      {syncingGoogle
                        ? "Sincronizando..."
                        : `Sincronizar Todos (${connectedCount}/${sales.length})`}
                    </Button>
                  )}

                  {disconnectedSales.map((s) => (
                    <Button
                      key={s.id}
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        connectGoogleOAuth(
                          () => {
                            fetchTasks();
                            notify(
                              `Google Agenda conectada para ${s.first_name}!`,
                              { type: "success" },
                            );
                          },
                          undefined,
                          s.id,
                        )
                      }
                      className="gap-1.5 h-8.5 text-xs font-semibold border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 shadow-xs cursor-pointer"
                      title={`Conectar Google Agenda de ${s.first_name}`}
                    >
                      <Calendar className="w-3.5 h-3.5 text-blue-500" />
                      Conectar Google ({s.first_name})
                    </Button>
                  ))}
                </div>
              );
            }

            // Modo filtrado por um vendedor específico
            const isTargetConnected = Boolean(
              currentSale && getConnectedGoogleUserBySalesId(currentSale.id),
            );
            const saleName = currentSale?.first_name || "Usuário";

            return isTargetConnected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSyncGoogleCalendar()}
                disabled={syncingGoogle}
                className="gap-1.5 h-8.5 text-xs font-semibold border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 shadow-xs cursor-pointer"
                title={`Sincronizar Google Agenda de ${saleName}`}
              >
                <RefreshCw
                  className={cn("w-3.5 h-3.5", syncingGoogle && "animate-spin")}
                />
                {syncingGoogle
                  ? "Sincronizando..."
                  : `Sincronizar (${saleName})`}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  connectGoogleOAuth(
                    () => {
                      fetchTasks();
                      notify(`Google Agenda conectada para ${saleName}!`, {
                        type: "success",
                      });
                    },
                    undefined,
                    selectedSalesId,
                  )
                }
                className="gap-1.5 h-8.5 text-xs font-semibold border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 shadow-xs cursor-pointer"
              >
                <Calendar className="w-3.5 h-3.5 text-blue-500" />
                Conectar Google ({saleName})
              </Button>
            );
          })()}

          <Button
            size="sm"
            onClick={handleCreateNew}
            className="gap-1.5 h-8.5 text-xs font-semibold shadow-xs"
          >
            <Plus className="w-4 h-4" />
            Nova Atividade
          </Button>
        </div>
      </div>

      {/* Navigation & Filter Bar (apenas quando não estiver no modo Lista) */}
      {viewMode !== "list" && (
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 bg-card/80 p-3 rounded-2xl border shadow-xs">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              className="h-8 text-xs font-semibold"
            >
              Hoje
            </Button>

            <div className="flex items-center border rounded-lg bg-muted/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="h-4 w-px bg-border" />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNext}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Próximo"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2 pl-2">
              <CalendarIcon className="w-4 h-4 text-primary" />
              <span className="text-sm font-bold capitalize tracking-tight">
                {getHeaderTitle()}
              </span>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
            {/* Menu Suspenso de Usuários da Equipe */}
            <div className="flex items-center gap-1.5 bg-muted/60 p-0.5 rounded-xl border border-border/70">
              <Users className="w-3.5 h-3.5 text-primary ml-1.5 shrink-0" />
              <Select
                value={selectedSalesId}
                onValueChange={setSelectedSalesId}
              >
                <SelectTrigger className="h-8 text-xs font-semibold min-w-[190px] max-w-[270px] bg-background border-none shadow-none">
                  <SelectValue placeholder="Selecione o usuário..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs font-medium">
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-bold">
                        👥
                      </span>
                      <span>Toda a Equipe (Todos)</span>
                    </div>
                  </SelectItem>
                  {sales.map((sale) => {
                    const isMe =
                      identity?.id && String(identity.id) === String(sale.id);
                    return (
                      <SelectItem
                        key={sale.id}
                        value={String(sale.id)}
                        className="text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                            {sale.first_name?.[0] || "U"}
                          </span>
                          <span className="font-semibold">
                            {sale.first_name} {sale.last_name || ""}
                          </span>
                          {isMe && (
                            <span className="text-[10px] px-1 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold border border-emerald-500/20">
                              Meu
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1 text-muted-foreground mr-1">
              <Filter className="w-3.5 h-3.5" />
              <span className="text-[11px] font-medium uppercase tracking-wider hidden sm:inline">
                Filtros:
              </span>
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={(val) => setStatusFilter(val as any)}
            >
              <SelectTrigger className="h-8 text-xs min-w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Todas as etapas
                </SelectItem>
                <SelectItem value="pending" className="text-xs">
                  Pendentes
                </SelectItem>
                <SelectItem value="done" className="text-xs">
                  Concluídas
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 text-xs min-w-[140px]">
                <SelectValue placeholder="Tipo de Atividade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Todos os tipos
                </SelectItem>
                {Object.values(ACTIVITY_TYPES).map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className="text-xs"
                  >
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Priority Filter */}
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-8 text-xs min-w-[140px]">
                <SelectValue placeholder="Prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Todas as prioridades
                </SelectItem>
                <SelectItem value="alta" className="text-xs">
                  Prioridade Alta
                </SelectItem>
                <SelectItem value="media" className="text-xs">
                  Prioridade Média
                </SelectItem>
                <SelectItem value="baixa" className="text-xs">
                  Prioridade Baixa
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Main Agenda View */}
      {loading ? (
        <div className="p-16 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span>Carregando agenda...</span>
        </div>
      ) : viewMode === "day" ? (
        <AgendaDayView
          currentDate={currentDate}
          tasks={filteredTasks}
          onEditTask={handleEditTask}
          onSelectSlot={handleSelectSlot}
          onRefresh={() => fetchTasks(true)}
          onSelectDate={setCurrentDate}
        />
      ) : viewMode === "week" ? (
        <AgendaWeekView
          currentDate={currentDate}
          tasks={filteredTasks}
          onEditTask={handleEditTask}
          onSelectDate={handleSelectSlot}
          onRefresh={() => fetchTasks(true)}
          salesList={sales}
        />
      ) : viewMode === "month" ? (
        <AgendaMonthView
          currentDate={currentDate}
          tasks={filteredTasks}
          onEditTask={handleEditTask}
          onSelectDate={handleSelectSlot}
          onRefresh={() => fetchTasks(true)}
        />
      ) : (
        <AgendaListView
          tasks={tasks}
          salesList={sales}
          dealsMap={dealsMap}
          contactsMap={contactsMap}
          companiesMap={companiesMap}
          selectedSalesId={selectedSalesId}
          onSelectSalesId={setSelectedSalesId}
          onEditTask={handleEditTask}
          onRefresh={() => fetchTasks(true)}
          onCreateNew={handleCreateNew}
        />
      )}

      {/* Dialog */}
      <ActivityDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        taskToEdit={selectedTask}
        defaultDate={defaultDialogDate}
        defaultSalesId={
          selectedSalesId !== "all" ? selectedSalesId : identity?.id
        }
        onSuccess={() => fetchTasks(true)}
      />
    </div>
  );
};
